/**
 * @file The listing half of form point-in-time recovery (SYD-2450): calls
 * `listFormVersionRestorePoints` for the open form version and
 * `listArTableRestorePoints` for every table its layout holds, merges the answers
 * into one timeline and puts it onto `formRecoveryStore` at this call site. It also
 * calls `listFormVersionRestorePointsBetween` to splice the saves lying in one gap
 * into the version ladder, then re-merges. Consumer: `FormRecoveryGroup`.
 *
 * The client composes; there is no combined callable. Nothing on the server would
 * be duplicated by one, and a restore of a form and its tables cannot be atomic —
 * they are a document and N collections, and Firestore gives atomicity within each
 * and not across them. Composing here keeps the partial outcome visible in the one
 * place that has to report it.
 *
 * The version's ladder is fatal: without the form's own history there is no
 * timeline for the tables to hang on. A table's is not — its refusal is recorded,
 * named beside the list and counted into `refusedMomentCount`, the same way a
 * refused rung already coarsens the ladder rather than throwing. A step a refinement's
 * walk could not read is the same fact one gap smaller: it ends that walk at
 * `end: 'floor'` with its rows in hand, so the gap keeps a live control and the count
 * joins the version's, which is what puts `Retry` back within reach.
 *
 * It lives in a hook rather than in the store because the store is reached through
 * `store/index.ts`: importing a callable wrapper there would initialize Firebase
 * Functions for every consumer of the store barrel, tests included.
 *
 * The ladder is the whole list, so `load` is a no-op once it has landed and a
 * ladder response replaces `points` rather than appending to it. A refinement is
 * the only thing that grows the list, and it grows it in place: its rows lie
 * strictly between the pair it was asked about, so they are spliced directly after
 * the later row and the list stays strictly decreasing.
 *
 * A ladder's answer is told from another's by `listRequestId`, raised on the store
 * before each request and captured beside the ids: two ladder requests for one
 * version are identical, so nothing in the request itself could tell them apart.
 * A refinement captures the same counter, because its rows were measured against
 * the gaps of the ladder that was on screen when it was sent — splicing them into
 * a list a later ladder built would put the list out of order, since the second
 * ladder sampled at a later `now` and the row below the pressed one may have moved.
 * `refiningLaterCursorMicros` is its second token, and what holds refinements to
 * one at a time. A refinement may only start on a `loaded` ladder, and every path that
 * asks for a ladder drops the refinements with it — the two together are what keep a
 * gap measured against one ladder out of the next.
 *
 * `refresh` is the one path that re-lists what is already loaded, and only a restore
 * or an undo reaches it — each an edit the operator made, which produces a restore
 * point of its own that no other path would ever show them.
 */

import { useCallback } from 'react';
import { useSnapshot } from 'valtio';

import type {
  ArTableId,
  FormRestorePoint,
  FormRestorePointsBetweenEnd,
  ListFormVersionRestorePointsBetweenRequest,
  ListFormVersionRestorePointsRequest,
} from '#types';
import { listArTableRestorePoints } from '@/services/arTablePitrFunctions';
import {
  listFormVersionRestorePoints,
  listFormVersionRestorePointsBetween,
} from '@/services/formPitrFunctions';
import type {
  RecoveryTableList,
  RecoveryTableListError,
} from '@/store/form/formRecoveryStore';
import { readCallableError } from '@/workflows/runtime/readCallableError';

import { formRecoveryStore, formStore } from '../../store';
import { mergeRecoveryMoments } from './mergeRecoveryMoments';
import { useFormTableIds } from './useFormTableIds';

/**
 * Rows one press of `Show saves in between` asks for. Each row is a sequential
 * round trip on the server, which clamps this to 25; ten is what one gap in a
 * seven-day ladder is worth paying for in a single press, and `end: 'limit'`
 * leaves the rest reachable from the row the refinement stopped on.
 */
const REFINEMENT_LIMIT = 10;

export type FormRecoveryList = {
  /** The page names a form version, so there is a document to sample. */
  canList: boolean;
  /**
   * Lists the ladder. A no-op once it has landed on the open version, however it
   * ended; a version the loaded list does not describe restarts it.
   */
  load: () => Promise<void>;
  /**
   * Lists the ladder again after one that did not fully land: a refused request, or
   * a request that landed with moments the store would not read. A no-op on a clean
   * `loaded` list, which re-listing would only re-bill.
   */
  retry: () => Promise<void>;
  /**
   * Lists the saves strictly between two adjacent rows and splices them in after
   * the later one. `earlierCursorMicros` is `null` for the gap below the deepest
   * row. A no-op while another refinement is in flight, and while the ladder those
   * rows would be spliced into is still being listed.
   */
  refine: (laterCursorMicros: number, earlierCursorMicros: number | null) => Promise<void>;
  /**
   * Drops every loaded row and lists the ladder again. The restore and the undo
   * call it, because each writes a version and so adds a restore point the loaded
   * list can never reach. Nothing else may: it re-bills the whole ladder.
   */
  refresh: () => Promise<void>;
};

/**
 * Whether the page is still waiting for exactly this ladder. Everything compared
 * against was captured before the await — the ids the request names and the token it
 * was sent under — so this is the guard the "never read the bare proxy after an
 * await" rule exists to make possible, not an exception to it.
 *
 * A version switch (which `formStore` answers by calling `formRecoveryStore.clear()`),
 * a clear, or a refresh each move one of these, and the late response is dropped
 * rather than written over a list the user is no longer looking at.
 */
const stillWanted = (request: ListFormVersionRestorePointsRequest, requestId: number): boolean =>
  formStore.state.formId === request.formId &&
  formStore.state.versionId === request.formVersionId &&
  formRecoveryStore.state.listStatus === 'loading' &&
  formRecoveryStore.state.listRequestId === requestId;

/**
 * The same guard for a refinement, and it takes two tokens rather than one.
 * `refiningLaterCursorMicros` identifies the refinement — only one runs at a time,
 * and `clear`, `refresh` and every listing return it to `null`. `listRequestId`
 * identifies the ladder its rows were measured against: a restore refreshes the list, and a
 * refinement issued against the ladder before it would otherwise splice rows sampled
 * at an older `now` into the new one, out of order and against a row key already on
 * screen.
 */
const refinementStillWanted = (
  request: ListFormVersionRestorePointsBetweenRequest,
  requestId: number,
): boolean =>
  formStore.state.formId === request.formId &&
  formStore.state.versionId === request.formVersionId &&
  formRecoveryStore.state.listRequestId === requestId &&
  formRecoveryStore.state.refiningLaterCursorMicros === request.laterCursorMicros;

/**
 * The later rows a landed refinement proves have nothing left below them: the bound
 * it was asked about and every row it returned. Two answers leave the last row out,
 * because the walk stopped ON it rather than below it: `'limit'`, the read cap, and a
 * step the store refused, which reports `end: 'floor'` — byte-identical to a genuine
 * retention floor — and is told from it by `refusedMomentCount` alone. In both the walk
 * resumes from that row, so its own control stays live and a second press continues the
 * gap instead of re-walking rows already spliced in.
 *
 * A short walk that returned no row at all proves nothing, not even about the bound.
 */
const exhaustedBy = (
  laterCursorMicros: number,
  points: readonly FormRestorePoint[],
  end: FormRestorePointsBetweenEnd,
  refusedMomentCount: number,
): number[] => {
  const stoppedShort = end === 'limit' || refusedMomentCount > 0;
  if (stoppedShort && points.length === 0) return [];
  return [
    laterCursorMicros,
    ...(stoppedShort ? points.slice(0, -1) : points).map((point) => point.cursorMicros),
  ];
};

/** Whether the timeline on screen was listed against exactly the layout's tables. */
const sameTableIds = (covered: readonly ArTableId[], wanted: readonly ArTableId[]): boolean =>
  covered.length === wanted.length && covered.every((tableId, index) => tableId === wanted[index]);

/**
 * Rebuilds `moments` from the lists already on the store. Called after a refinement
 * has spliced rows into the version ladder, which is the one path that changes a
 * source without re-listing it. It reads the bare proxy, and may: every caller is
 * past its own post-await guard and this runs synchronously inside that check.
 *
 * `versionEnd` is `null` only before any ladder has landed, which no refinement can
 * reach.
 */
const remergeMoments = (): void => {
  const { points, versionEnd, versionRefusedMomentCount, tableLists, tableListErrors } =
    formRecoveryStore.state;
  if (versionEnd === null) return;

  const merged = mergeRecoveryMoments({
    version: { points, end: versionEnd, refusedMomentCount: versionRefusedMomentCount },
    tables: tableLists,
    refusedTableCount: tableListErrors.length,
  });

  formRecoveryStore.state.moments = merged.moments;
  formRecoveryStore.state.end = merged.end;
  formRecoveryStore.state.refusedMomentCount = merged.refusedMomentCount;
};

export const useFormRecoveryList = (): FormRecoveryList => {
  const snap = useSnapshot(formStore.state);
  const formId = snap.formId;
  const formVersionId = snap.versionId;
  const tableIds = useFormTableIds();

  /**
   * The version's ladder and one per table, all in flight together. Both branches are
   * `Promise.allSettled`, including the single-element one: it attaches its handlers
   * the moment the call is made, so a version refused while the tables are still out
   * is never an unhandled rejection waiting on them.
   *
   * Every refinement is dropped here rather than in `refresh` alone. A gap is a pair of
   * cursors in the ladder being replaced, so the moment a new ladder is asked for the
   * gaps addressed against the old one mean nothing — and the refinement in flight,
   * which this request's own token has just retired, would otherwise leave its control
   * spinning and every other gap reading `Another gap is being listed` until the next
   * version switch.
   *
   * The whole body sits inside the `catch` all the same. `allSettled` cannot reject,
   * so the only thing that reaches it is a malformed response — and a panel that says
   * it could not load the history is a better answer to that than a rejection nobody
   * handles.
   */
  const list = useCallback(async (): Promise<void> => {
    if (formId === undefined || formVersionId === undefined) return;
    const request: ListFormVersionRestorePointsRequest = { formId, formVersionId };
    const requestedTableIds: ArTableId[] = [...tableIds];

    formRecoveryStore.state.listStatus = 'loading';
    formRecoveryStore.state.listError = null;
    formRecoveryStore.state.refusedMomentCount = 0;
    formRecoveryStore.state.tableIds = requestedTableIds;
    formRecoveryStore.state.tableLists = [];
    formRecoveryStore.state.tableListErrors = [];
    formRecoveryStore.state.refiningLaterCursorMicros = null;
    formRecoveryStore.state.exhaustedLaterCursors = [];
    formRecoveryStore.state.refineError = null;
    formRecoveryStore.state.listVersionId = formVersionId;
    formRecoveryStore.state.listRequestId += 1;
    const requestId = formRecoveryStore.state.listRequestId;

    try {
      const [[versionSettled], tablesSettled] = await Promise.all([
        Promise.allSettled([listFormVersionRestorePoints(request)]),
        Promise.allSettled(
          requestedTableIds.map((tableId) =>
            listArTableRestorePoints({ formId, formVersionId, tableId }),
          ),
        ),
      ]);
      if (!stillWanted(request, requestId)) return;
      if (versionSettled.status === 'rejected') throw versionSettled.reason;

      const version = versionSettled.value;
      const tableLists = requestedTableIds.flatMap<RecoveryTableList>((tableId, index) => {
        const settled = tablesSettled[index];
        return settled.status === 'fulfilled'
          ? [
              {
                tableId,
                points: settled.value.points,
                end: settled.value.end,
                refusedMomentCount: settled.value.refusedMomentCount,
              },
            ]
          : [];
      });
      const tableListErrors = requestedTableIds.flatMap<RecoveryTableListError>(
        (tableId, index) => {
          const settled = tablesSettled[index];
          return settled.status === 'rejected'
            ? [{ tableId, message: readCallableError(settled.reason).message }]
            : [];
        },
      );

      const merged = mergeRecoveryMoments({
        version: {
          points: version.points,
          end: version.end,
          refusedMomentCount: version.refusedMomentCount,
        },
        tables: tableLists,
        refusedTableCount: tableListErrors.length,
      });

      formRecoveryStore.state.listStatus = 'loaded';
      formRecoveryStore.state.points = version.points;
      formRecoveryStore.state.versionEnd = version.end;
      formRecoveryStore.state.versionRefusedMomentCount = version.refusedMomentCount;
      formRecoveryStore.state.tableLists = tableLists;
      formRecoveryStore.state.tableListErrors = tableListErrors;
      formRecoveryStore.state.moments = merged.moments;
      formRecoveryStore.state.end = merged.end;
      formRecoveryStore.state.refusedMomentCount = merged.refusedMomentCount;
      formRecoveryStore.state.window = version.window;
      formRecoveryStore.state.liveCreatedAtMillis = version.liveCreatedAtMillis;
    } catch (error) {
      if (!stillWanted(request, requestId)) return;

      formRecoveryStore.state.listStatus = 'failed';
      formRecoveryStore.state.listError = readCallableError(error).message;
    }
  }, [formId, formVersionId, tableIds]);

  /**
   * `idle` is the only state the ladder may start from on a version the loaded list
   * already describes. The group remounts every time the sidebar drawer opens, and
   * re-listing on each of those would bill the same reads again and throw away every
   * gap the operator had refined.
   *
   * A version the list does not describe restarts it whatever the status says. The
   * mount effect and `formStore`'s own `formRecoveryStore.clear()` are two listeners
   * on one change and nothing orders them, so an effect that ran first would otherwise
   * find `loaded` and no-op, and the panel would sit on the previous version's rows
   * until the drawer was closed and reopened.
   *
   * Tables the loaded timeline does not cover restart it for the same reason. The form
   * type carrying the layout lands on its own listener, so a first `load` can easily
   * run against no tables at all; leaving that timeline in place would tell the
   * operator the tables never changed.
   */
  const load = useCallback((): Promise<void> => {
    if (formVersionId === undefined) return Promise.resolve();
    const { listStatus, listVersionId, tableIds: covered } = formRecoveryStore.state;
    if (listVersionId === formVersionId) {
      if (listStatus === 'idle') return list();
      return sameTableIds(covered, tableIds) ? Promise.resolve() : list();
    }
    formRecoveryStore.clear();
    return list();
  }, [list, formVersionId, tableIds]);

  /**
   * A ladder that landed with refused moments is `loaded` and short: `load` no-ops on
   * it and `refresh` is the restore's alone, so without this the panel would carry the
   * hole for the life of the session. A clean `loaded` list stays out — re-listing it
   * re-bills every rung for nothing.
   */
  const retry = useCallback((): Promise<void> => {
    const { listStatus, refusedMomentCount } = formRecoveryStore.state;
    if (listStatus !== 'failed' && refusedMomentCount === 0) return Promise.resolve();
    return list();
  }, [list]);

  /**
   * The returned rows lie strictly between the pair, so the splice point is the later
   * row's own index: the list stays strictly decreasing without a sort. A later row
   * the splice can no longer find is a list that moved under the call, and the rows
   * are dropped rather than guessed at.
   *
   * The bounds are version cursors and the splice is into the version ladder, because
   * a gap is a pair of saves and only that ladder can be walked between two of them.
   * The timeline is rebuilt afterwards: a spliced save may fall in a minute a table
   * already reported, and then the two are one moment.
   *
   * A step the walk could not read joins `versionRefusedMomentCount` before that rebuild,
   * rather than being written onto the merged count the rebuild recomputes from the
   * sources. That is also the one thing that reopens `retry`, which a `loaded` list
   * refuses until some source reports a moment it could not read.
   *
   * Only a `loaded` ladder has gaps to refine. `listRequestId` is raised when a ladder
   * is asked for, not when it lands, so a refinement started during a listing would
   * capture the new ladder's own token, pass its post-await guard, and splice rows
   * measured against the ladder that is being replaced — duplicate cursors, broken
   * ordering, and a genuine gap marked spent. Retry then a gap is that ordering, and it
   * needs no exotic timing at all.
   */
  const refine = useCallback(
    async (laterCursorMicros: number, earlierCursorMicros: number | null): Promise<void> => {
      if (formId === undefined || formVersionId === undefined) return;
      if (formRecoveryStore.state.listStatus !== 'loaded') return;
      if (formRecoveryStore.state.refiningLaterCursorMicros !== null) return;
      const request: ListFormVersionRestorePointsBetweenRequest = {
        formId,
        formVersionId,
        laterCursorMicros,
        earlierCursorMicros,
        limit: REFINEMENT_LIMIT,
      };

      formRecoveryStore.state.refiningLaterCursorMicros = laterCursorMicros;
      formRecoveryStore.state.refineError = null;
      const requestId = formRecoveryStore.state.listRequestId;

      try {
        const response = await listFormVersionRestorePointsBetween(request);
        if (!refinementStillWanted(request, requestId)) return;

        const at = formRecoveryStore.state.points.findIndex(
          (point) => point.cursorMicros === laterCursorMicros,
        );
        formRecoveryStore.state.refiningLaterCursorMicros = null;
        if (at === -1) return;

        formRecoveryStore.state.points = [
          ...formRecoveryStore.state.points.slice(0, at + 1),
          ...response.points,
          ...formRecoveryStore.state.points.slice(at + 1),
        ];
        formRecoveryStore.state.exhaustedLaterCursors = [
          ...formRecoveryStore.state.exhaustedLaterCursors,
          ...exhaustedBy(
            laterCursorMicros,
            response.points,
            response.end,
            response.refusedMomentCount,
          ),
        ];
        formRecoveryStore.state.versionRefusedMomentCount += response.refusedMomentCount;
        formRecoveryStore.state.window = response.window;
        remergeMoments();
      } catch (error) {
        if (!refinementStillWanted(request, requestId)) return;

        formRecoveryStore.state.refiningLaterCursorMicros = null;
        formRecoveryStore.state.refineError = {
          laterCursorMicros,
          message: readCallableError(error).message,
        };
      }
    },
    [formId, formVersionId],
  );

  /**
   * The timeline again, from nothing — every source, not just the version's. It
   * resets the listing alone: `clear()` would take `restoredFrom` with it, and the
   * banner naming what the restore wrote has to survive the re-list that same restore
   * triggers. Every refinement goes with the rows it produced — the gaps are addresses
   * into a list that no longer exists.
   */
  const refresh = useCallback((): Promise<void> => {
    if (formVersionId === undefined) return Promise.resolve();
    formRecoveryStore.state.points = [];
    formRecoveryStore.state.tableIds = [];
    formRecoveryStore.state.tableLists = [];
    formRecoveryStore.state.tableListErrors = [];
    formRecoveryStore.state.moments = [];
    formRecoveryStore.state.listStatus = 'idle';
    formRecoveryStore.state.listError = null;
    formRecoveryStore.state.end = null;
    formRecoveryStore.state.versionEnd = null;
    formRecoveryStore.state.versionRefusedMomentCount = 0;
    return list();
  }, [list, formVersionId]);

  return {
    canList: formId !== undefined && formVersionId !== undefined,
    load,
    retry,
    refine,
    refresh,
  };
};
